import { Address, Redirect } from "./address";
import { BaseResponse, InstallationOptions } from "./common";
import { BillingSummary, Contact } from "./contact-credit";
import { LegalMap } from "./legal";
import { OfferObject } from "./offerObject";
import { Disclosure } from "./order";
import { DynamicMerchandisingConfig } from "../../monarch/Config | Dynamic Merchandising/default";
import { InputHTMLAttributes } from "react";
import type { GdtAnswer } from "./recommendation/multi-recommendation";

export interface RecommendedVAS {
  id: string;
  name: string;
  scores: number;
}

export interface PersonalizedScores {
  Coverage: number;
  Entertainment: number;
  Security: number;
  Speed: number;
}

export interface DimScoresViz {
  Coverage: number;
  Entertainment: number;
  Security: number;
  Speed: number;
}

export interface Recommendation {
  id: string;
  name: string;
  personalizedScores: PersonalizedScores;
  percentMatch: number;
  nationalScores: PersonalizedScores;
  dimScoresViz: DimScoresViz;
  recommendedVAS: RecommendedVAS[];
}

export interface RankedSpeed {
  id: string;
  name: string;
  percentMatch: number;
  speedScore: number;
}

export interface RecommendationsData {
  recommendations: Recommendation[];
  rankedVAS: RecommendedVAS[];
  rankedSpeeds: RankedSpeed[];
  isDefault: boolean;
}
export interface RTBBullet {
  bullet: string;
  isPositive: boolean;
}

export interface RecommendedRTB {
  productId: string;
  productName: string;
  bullets: RTBBullet[];
  recommendationReason?: string;
  source?: "altProductExplainer" | "productExplainer" | "default";
}

export interface DiscoveryToolAnswers {
  answers: GdtAnswer[];
}
export interface TopRecommendationsResponse extends BaseResponse {
  recommendedProducts?: RecommendationsData;
}

export interface DynamicRTBsResponse extends BaseResponse {
  recommendedRTBs?: Record<string, RecommendedRTB>;
}

export interface GdtAnswerPredictorResponse extends BaseResponse {
  discoveryToolAnswers?: DiscoveryToolAnswers;
}

export interface GetProductsResponse extends BaseResponse {
  quoteId: string;
  quoteCreated?: string;
  quoteNumber: string;
  accountUuid: string;
  availableProducts: Product[];
  addressHasExistingService: boolean;
  legal: LegalMap;
  customer: Omit<Contact, "firstname" | "lastname"> & { firstName: string; lastName: string };
  gmtOffset?: number;
  serviceAddress: Address;
  unservReason?: string;
  firstAvailable: string;
  productList?: OfferObject[];
  defaultOffers?: OfferBannerObject[];
  salesJourney?: string;
  techAvailable?: string;
  offerType?: string;
  redirect?: Redirect;
}

export interface AddonMetadata {
  title?: string;
  priceTooltip?: string;
  subpriceDisclaimer?: string;
  longDescription?: string;
  longDescriptionLegalKey?: string;
  shortDescription?: string;
  bannerCopy?: string;
  bannerIcon?: string;
  isFeatured?: boolean;
  order?: string;
  category?: string;
  vasCardDropdownFeatureList?: FeatureObject[];
}
export interface ConfigurableAddOn extends AddonMetadata {
  addOnName: string; // key associated with the add-on from the `getAddon` function in products.ts
  product: Product;
  imageSrc: string;
  imageFocalPoint: string;
  sticker: {
    backgroundColor: string;
    textColor: string;
    text: string;
    enabled: boolean;
  };
  vasCardDropdownFeatureList?: FeatureObject[];
}

export interface VASFreeTrials {
  [key: string]: {
    freeTrialHash?: string;
    trialExperience?: boolean;
    version?: string;
    modalEnabled?: boolean;
  };
}

export interface ResumeQuoteResponse {
  success: boolean;
  created?: string;
  quoteId: string;
  rvoQuoteId?: boolean;
  quoteNumber: string;
  serviceAddress: Address;
  billingSummary: BillingSummary;
  creditChecked?: string;
  cartPage: string;
  accountUuid: string;
  sweetenerSelected?: boolean;
  selectedInternetProductId?: string;
  selectedVoiceProductId?: string;
  selectedVASProductIds?: Array<string>;
  selectedSecureProductIds?: Array<string>;
  selectedVideoProductIds?: Array<string>;
  installationOptions?: InstallationOptions;
  contactVerifyRequired?: boolean;
  isCreditFreezeStage?: boolean;
  creditFreezeSubmissionCount?: number;
  contactVerifyComplete?: boolean;
  salesJourney?: string;
  techAvailable?: string;
  offerType?: string;
}

export interface SelectProductsResponse {
  success: boolean;
  billingSummary: BillingSummary;
  installationOptions?: InstallationOptions;
  contactVerifyRequired: boolean;
  contactVerifyComplete?: boolean;
}

export interface PhoneVerificationResponse {
  success: boolean;
  message: string;
}

export interface PhoneVerificationRequest {
  code: string;
}

export interface PhoneVerificationResponse {
  success: boolean;
  message: string;
}

export interface PhoneVerificationRequest {
  code: string;
}

export interface CreditBypassResponse {
  success: boolean;
  billingSummary: BillingSummary;
  disclosures: Disclosure[];
}

export interface ContactGetResponse {
  success: boolean;
  accountUuid: string;
  hasAutoPay: string;
  hasPaperlessBill: string;
}

export interface SelectedCoupon {
  productCode: string;
  productId: string;
  amount: string;
  broadbandProductId: string;
}

export interface SelectedProductRequest {
  selectedProducts: SelectedProduct[];
  isLandingPage?: boolean;
  selectedCoupon?: SelectedCoupon;
}

export interface SelectedProduct {
  price?: Price;
  additionalOptions?: AdditionalOptions;
  productId: string;
  name?: string;
  type: string;
  dataspeed?: {
    downloadSpeedMbps: number;
    uploadSpeedMbps: number;
  };
  configurations?: SelectedProductConfiguration[];
}

export interface SelectedProductConfiguration {
  productId?: string;
  productKey: string;
  name?: string;
  active: boolean;
  amount?: number;
}

export interface CarouselProps {
  productIndex: number;
  paginate: (index: number) => void;
  direction: number;
}

export type Option = {
  value: string;
  label: string;
};

export interface ProductFiltersProps {
  options: Option[];
  setOptions: (options: Option[]) => void;
  sweetener: Option;
  setSweetener: (selected: Option) => void;
  voiceAvailable: boolean;
  handleAddHomePhone: (event: InputHTMLAttributes<HTMLInputElement>) => void;
  handleAddProductState: (product: Product, option?: Product) => void;
  addPhoneState: boolean;
}

export type Feature = {
  icon?: string;
  text: string;
  legal?: string;
  bold?: boolean;
  tooltip?: string;
  outline?: boolean;
  tooltipCopy?: string;
  isPositive?: boolean;
};

export type SweetenerBanner = {
  text: string;
  legal: string;
  bold: boolean;
};

export interface AmountWithSweetenerType {
  [key: string]: number;
}

export type Price = {
  amount: number;
  amountMajor?: number;
  amountMinor?: number;
  amountNoAutopay?: number;
  originalAmount?: number;
  amountACH?: number;
  amountDebit?: number;
  amountCredit?: number;
  amountWithSweetener?: AmountWithSweetenerType;
  termedDiscountAmount?: number;
  overrideCode?: string;
};

export type Sweetener = {
  name: string;
  amount: number;
  legal: string;
  key?: string;
  productId?: string;
  productCode?: string;
  duration?: number;
};

export type OfferBannerObject = {
  productName: string;
  offerType: string;
  offerText: string;
  market: string;
  offerPlacement: string;
  offerRank: number;
  expirationDate?: string;
  legal?: string;
  offerBannerText?: string;
};

interface DetailsBannerItem {
  name: string;
  text: string;
}

export type Dataspeed = {
  uploadInKbps: number;
  downloadInKbps: number;
  uploadInMbps: number;
  downloadInMbps: number;
};

type ConfigurationsOptions = {
  active: boolean;
  id: string;
  name: string;
  displayName: string;
  price: string;
  amount?: number;
};

export type Configurations = {
  productId: string;
  name: string;
  options: ConfigurationsOptions[];
  active: boolean;
  productKey: string;
  amount?: number;
};

export type AdditionalOptions = {
  productId: string;
  name: string;
  displayName: string;
  description: string | Record<string | number, string>;
  maxAmount?: Record<string | number, number>;
  type: string;
  displayType: string;
  price: Price;
  productKey: string;
  selected?: boolean;
  amount?: number;
};

export type Product = {
  productId: string;
  name: string;
  description: string | Record<string | number, string>;
  descriptionTooltip?: string | null;
  maxAmount?: Record<string | number, number>;
  dataspeed: Dataspeed;
  type?: string;
  serviceType?: string;
  price: Price;
  features: Feature[];
  displayType: string;
  sweeteners: {
    [key: string]: Sweetener;
  };
  topOffer?: OfferBannerObject;
  vrcOffer?: OfferBannerObject;
  is200?: boolean;
  is500?: boolean;
  is1Gig?: boolean;
  is2Gig?: boolean;
  is5Gig?: boolean;
  is7Gig?: boolean;
  shortLegal: string;
  shortLegalNetflix?: string;
  detailsBanner: DetailsBannerItem[];
  configurations?: Configurations[];
  productConfigurations?: Configurations[];
  additionalOptions?: AdditionalOptions[];
  vasCardDropdownFeatureList?: FeatureObject[];
  disclaimer?: string;
  tooltip?: string;
  productKey?: string;
  promo?: string;
  displayStrikethrough?: boolean;
  displayName?: string;
  labelId?: string;
  netflixToggleEnabled?: boolean;
  isVrcSelected?: boolean;
  category?: string;
  sticker?: {
    enabled?: boolean;
    text?: string;
  };
  vasCardIconTag?: {
    vasCardIconTagCopy: string;
  }[];
  vasCardOuterBanner?: {
    vasCardOuterBannerCopy?: string;
    vasCardOuterBannerCopyColor?: string;
    vasCardOuterBannerBackgroundColor?: string;
  };
  shortDescription?: string;
};

export type SelectedProductsState = {
  internet: Product | undefined;
  voice: Product | undefined;
  secure?: {
    [key: string]: Product | SelectedProduct;
  };
  vas?: {
    [key: string]: Product;
  };
  video?:
    | {
        [key: string]: Product;
      }
    | boolean;
};

export type SelectedResumeProductsState = {
  internet: Product | undefined;
  voice: Product | undefined;
  secure?: {
    [key: string]: Product;
  };
  vas?: {
    [key: string]: Product;
  };
  video?: {
    [key: string]: Product;
  };
};

// Redux
export enum ActionTypes {
  SELECT_PRODUCT = "product/select",
  CLEAR_SELECT_PRODUCT = "product/select/clear",
  SET_PRODUCTS = "products/set",
  SET_OFFER_TYPE = "products/offerType/set",
  SET_SELECTED_SWEETENER = "products/planPageSelections/selectedSweetener/set",
  SET_NETFLIX_BUNDLE = "products/planPageSelections/selectedNetflix/set",
  SET_VRC_MODAL_SHOWN = "products/planPageSelections/vrcModalShown/set",
  SET_PHONE_SELECTION = "products/planPageSelections/phoneSelection/set",
  SET_FIRST_AVAILABLE = "products/firstAvailable/set",
  SET_NDI_BANNER = "products/ndiBanner/set",
  SET_LANDING_PAGE_PRODUCT = "products/landingPageProduct/set",
  SET_LANDING_PAGE_BUNDLE = "products/landingPageBundle/set",
  SET_LANDING_PAGE_PRODUCT_AVAILABLE = "products/landingPageProductAvailable/set",
  SET_OFFER_OBJECTS = "products/offerObjects/set",
  SET_VAS_SETTINGS = "products/vasSettings/set",
  SET_SALES_JOURNEY = "products/salesJourney/set",
  SET_IS_COPPER_2_FIBER = "products/isCopper2Fiber/set",
  SET_BAU_RECOMMENDATIONS = "products/bauRecommendations/set",
  SET_DYNAMIC_RTB = "products/dynamicRTB/set",
  SET_DISCOVERY_TOOL_ANSWERS = "products/discoveryToolAnswers/set",
  SET_DYNAMIC_MERCH_CONFIG = "products/dynamicMerchConfig/set",
}

export enum SweetenerKeys {
  EERO = "eero",
  VRC = "vrc",
  WAIVE_INSTALL = "waive-install-charge",
  WIFI_SECURITY_FREE_TRIAL = "wifi-security-free-trial",
  WHOLE_HOME_WIFI_FREE_TRIAL = "whole-home-wifi-free-trial",
  UNBREAKABLE_WIFI_FREE_TRIAL = "unbreakable-wifi-free-trial",
  FIBER_PROMOTIONAL_DISCOUNT = "fiber-promotional-discount",
  /** FRAPI COUPON / Samsung Best Buy — key must match fuel-ol `generateKey("COUPON")` (FRONT-12792) */
  SAMSUNG_BEST_BUY_COUPON = "samsung-best-buy-coupon",
}

export interface SetProductsAction {
  type: ActionTypes.SET_PRODUCTS;
  available: {
    [key: string]: Product[];
  };
  offerLevelSweeteners: {
    has2Gig: boolean;
    sweetenerLegalKeys: SweetenerKeys[];
  };
  offerObjects?: OfferObject[];
}

export interface SetOfferTypeAction {
  type: ActionTypes.SET_OFFER_TYPE;
  offerType: string;
}

export interface SetOfferObjectsAction {
  type: ActionTypes.SET_OFFER_OBJECTS;
  offerObjects: OfferObject[];
}

export interface SelectProductAction {
  type: ActionTypes.SELECT_PRODUCT;
  selectedState: SelectedProductsState | SelectedResumeProductsState;
}

export interface ClearSelectProductAction {
  type: ActionTypes.CLEAR_SELECT_PRODUCT;
}

export interface SetSelectedSweetenerAction {
  type: ActionTypes.SET_SELECTED_SWEETENER;
  selectedSweetener: Option;
}

export interface SetSelectedNetflixAction {
  type: ActionTypes.SET_NETFLIX_BUNDLE;
  selectedNetflix: boolean;
}

export interface SetVRCModalShown {
  type: ActionTypes.SET_VRC_MODAL_SHOWN;
  vrcModalShown: boolean;
}

export interface SetPhoneSelection {
  type: ActionTypes.SET_PHONE_SELECTION;
  phoneSelection: string;
}

export interface FirstAvailableAction {
  type: ActionTypes.SET_FIRST_AVAILABLE;
  firstAvailable: string;
}

export interface SetNDIBannerAction {
  type: ActionTypes.SET_NDI_BANNER;
  ndiBanner: boolean;
}

export interface SetLandingPageProduct {
  type: ActionTypes.SET_LANDING_PAGE_PRODUCT;
  landingPageProduct: string;
}

export interface SetLandingPageBundle {
  type: ActionTypes.SET_LANDING_PAGE_BUNDLE;
  landingPageBundle: LandingPageBundle;
}

export interface SetLandingPageProductAvailable {
  type: ActionTypes.SET_LANDING_PAGE_PRODUCT_AVAILABLE;
  landingPageProductAvailable: boolean;
}

export interface SetVasSettings {
  type: ActionTypes.SET_VAS_SETTINGS;
  hasVas: boolean;
  hasVasVersion: string;
  hasVasSubmitTextButton: string;
  vasAlias: string;
}

export interface SetSalesJourney {
  type: ActionTypes.SET_SALES_JOURNEY;
  salesJourney: string;
}

export interface SetIsCopper2FiberAction {
  type: ActionTypes.SET_IS_COPPER_2_FIBER;
  isCopper2Fiber: boolean;
}

export interface SetBauRecommendationsAction {
  type: ActionTypes.SET_BAU_RECOMMENDATIONS;
  recommendations: RecommendationsData;
}
export interface SetDynamicRTBAction {
  type: ActionTypes.SET_DYNAMIC_RTB;
  recommendedRTBs: Record<string, RecommendedRTB>;
}

export interface SetDiscoveryToolAnswersAction {
  type: ActionTypes.SET_DISCOVERY_TOOL_ANSWERS;
  discoveryToolAnswers: DiscoveryToolAnswers;
}
export interface SetDynamicMerchConfigAction {
  type: ActionTypes.SET_DYNAMIC_MERCH_CONFIG;
  dynamicMerchConfig: DynamicMerchandisingConfig;
}

export type Actions =
  | SetProductsAction
  | SelectProductAction
  | ClearSelectProductAction
  | SetOfferTypeAction
  | SetSelectedSweetenerAction
  | SetSelectedNetflixAction
  | SetVRCModalShown
  | SetPhoneSelection
  | FirstAvailableAction
  | SetNDIBannerAction
  | SetLandingPageProduct
  | SetLandingPageBundle
  | SetLandingPageProductAvailable
  | SetOfferObjectsAction
  | SetVasSettings
  | SetSalesJourney
  | SetIsCopper2FiberAction
  | SetBauRecommendationsAction
  | SetDynamicRTBAction
  | SetDiscoveryToolAnswersAction
  | SetDynamicMerchConfigAction;

export interface FeatureItem {
  text: string;
  legal?: string;
  outline?: boolean;
}

interface FeatureObject {
  [key: `feature${number}`]: FeatureItem;
}

interface BaseAddOnFeature {
  trialExperience?: {
    [key: string]: string | boolean;
  };
  category?: string;
}

interface NamedAddOnFeature extends BaseAddOnFeature {
  name: string;
  title?: string;
  addOnFeature?: FeatureObject[];
  longDescriptionLegalKey?: string;
  vasCardDropdownFeatureList?: FeatureObject[];
}

interface TitledAddOnFeature extends BaseAddOnFeature {
  title: string;
  name?: string;
  addOnFeature?: FeatureObject[];
  longDescriptionLegalKey?: string;
  vasCardDropdownFeatureList?: FeatureObject[];
}

export type AddOnFeature = NamedAddOnFeature | TitledAddOnFeature;

export interface PlanCardFeature {
  text: string;
  legal?: string;
  outline?: boolean;
  tooltipCopy?: string;
}

export interface MatchedProducts {
  [key: string]: Product;
}

export interface ProductCardTile {
  heading: string;
  deals: {
    copy: string;
    legal: string;
    icon: string;
    tooltip: string;
  }[];
}

export interface FlowOverride {
  name: string;
  enabled: boolean;
  copy: string; //TODO: remove fallback when OM components updated in prod
  staticCopy?: string;
  rewardsCopy?: string;
}

// TODO: UNIFICATION OFFER MERCH - Remove legacy types once new simplified metadata is fully implemented
export interface SimplifiedBanner {
  copy: string;
  copyColor: string;
  color: string;
  backgroundColor: string;
  hideOfferInSeeDetails?: boolean;
}

export interface IncludedSectionItem {
  icon?: string;
  iconColor?: string;
  copy?: string;
}
export interface VerizonSectionItem {
  copy: string;
}

export interface VerizonOfferSectionItem {
  copy: string;
  copyGreen?: string;
}
export interface VerizonOfferPill {
  icon?: string;
  iconColor?: string;
  copy?: string;
}

export interface SimplifiedModal {
  legalHashes: string[];
  includedSection?: IncludedSectionItem[];
  verizonOfferSection?: VerizonOfferSectionItem[];
  verizonOfferHeadline?: string;
  verizonOfferPill?: VerizonOfferPill;
  verizonSection?: VerizonSectionItem[];
  verizonHeadline?: string;
}

export interface AboveRTBTag {
  icon?: string;
  iconColor?: string;
  copy?: string;
  copyColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  modalEnabled?: {
    copy: string;
  };
}

export interface ltoBannerContactCredit {
  icon?: string;
  heading?: string;
  subheading?: string;
  modalCallToActionText?: string;
  discountModal?: {
    heading: string;
    disclaimers: string[];
  };
}

// TODO: UNIFICATION OFFER MERCH - Remove legacy types once new simplified metadata is fully implemented
export interface PlanCardsMetadata {
  [key: string]: {
    productCardHash?: string;
    name?: string;
    order?: number;
    visibility?: {
      hidden: boolean;
      userOverride: boolean;
    };
    bannerEnabled?: boolean;
    useBulletsWithFeatureList?: boolean;
    totalValueEnabled?: boolean;
    simplified: {
      simplifiedBanners?: SimplifiedBanner[]; // this contains the content of the banners // green Color inside card
      banners?: SimplifiedBanner[];
      modal?: SimplifiedModal;
      isTopBannerVisible?: boolean;
      productEmphasis?: boolean;
      adjustSizingCta?: boolean;
    };
    bestFor?: {
      enabled?: boolean;
      copy?: string;
    };
    bannerProductSelection?: {
      icon?: string;
    };
    outerBanner?: {
      userFlowScope: string;
      copy?: string;
      copyColor?: string;
      backgroundColor?: string;
      enabled?: boolean;
      outerBannerText?: string;
      outerBannerBackground?: string;
      outerBannerTextColor?: string;
      displayBannerOnSkipRewards?: boolean;
      flowOverrides?: FlowOverride[];
    };
    innerBanner?: {
      icon?: string;
      copy?: string;
      copyColor?: string;
      backgroundColor?: string;
      enabled?: boolean;
      innerBannerIcon?: string;
      innerBannerText?: string;
      innerBannerBackground?: string;
    };
    offerFlagLeft?: {
      copy?: string;
      icon?: string;
    };
    aboveRTBTag?: AboveRTBTag;
    offerFlagRight?: {
      copy?: string;
      copyColor?: string;
      backgroundColor?: string;
    };
    discount?: {
      copy?: string;
      copyColor?: string;
      textColor?: string;
      backgroundColor?: string;
    };
    featureList?: FeatureObject[];
    enabled?: boolean; // This covers both hideFeatureList and regular metadata
    expandOneFeature?: boolean; // on click shows just one feature
    bestValueBanner?: {
      copy?: string;
      textColor?: string;
      backgroundColor?: string;
    };
    vrcBanner?: {
      copy?: string;
      optOutCheckbox?: boolean;
    };
    flexTile?: ProductCardTile;
    bannerContactCredit?: {
      title?: string;
      subtitle?: string;
      backgroundColor?: string;
      modalEnabled?: {
        copy: string;
      };
    };
    ltoBannerContactCredit?: ltoBannerContactCredit;
    topRecommendedEnabled?: {
      copy?: string;
    };
    bannerOrderPage?: {
      title?: string;
      subtitle?: string;
      modalEnabled?: {
        copy: string;
      };
    };
    legalTooltip?: {
      [addressOfferType: string]: string; // e.g., 'HVOTest', 'addressCohort', 'default', '3monthfree', 'RVO'
    };
    ngcPackageTags?: string[];
  };
}

// Type for a single product card (extracted from PlanCardsMetadata)
export type ProductCard = PlanCardsMetadata[string];

export type ProductsActions =
  | SetProductsAction
  | SelectProductAction
  | ClearSelectProductAction
  | SetOfferTypeAction
  | SetSelectedSweetenerAction
  | SetSelectedNetflixAction
  | SetVRCModalShown
  | SetPhoneSelection
  | FirstAvailableAction
  | SetNDIBannerAction
  | SetLandingPageProduct
  | SetLandingPageBundle
  | SetLandingPageProductAvailable
  | SetOfferObjectsAction
  | SetVasSettings
  | SetSalesJourney
  | SetBauRecommendationsAction
  | SetDynamicRTBAction
  | SetDiscoveryToolAnswersAction
  | SetDynamicMerchConfigAction;

export interface LandingPageBundle {
  enabled: boolean;
  name: string;
}
